// stereo-core: stereographic projections, unit primitive circle (R = 1).
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, unit } from "./vec.js";

export type Method = "equal-area" | "equal-angle";
export type Hemisphere = "lower" | "upper";
export interface Projection { method: Method; hemisphere: Hemisphere }
export interface XY { x: number; y: number }  // x = East, y = North (math orientation)

// [PRJ-03]
/** Flip an axial vector into the plotting hemisphere. Returns [vector, flipped]. */
export function toHemisphere(v: Vec3, h: Hemisphere): [Vec3, boolean] {
  const wantDown = h === "lower";
  const flip = wantDown ? v[2] > 0 : v[2] < 0;
  return [flip ? [-v[0], -v[1], -v[2]] : v, flip];
}

// [PRJ-01] [PRJ-02]
function radius(theta: number, m: Method) {
  return m === "equal-area" ? Math.SQRT2 * Math.sin(theta / 2) : Math.tan(theta / 2);
}
function thetaOf(r: number, m: Method) {
  return m === "equal-area" ? 2 * Math.asin(Math.min(1, r / Math.SQRT2)) : 2 * Math.atan(r);
}

/** Project a (unit or not) vector; axial, so both senses land on the same point. */
export function project(v: Vec3, p: Projection): XY {
  const [w] = toHemisphere(unit(v), p.hemisphere);
  const theta = Math.acos(Math.min(1, Math.abs(w[2])));  // angle from the vertical
  const h = Math.hypot(w[0], w[1]);
  if (h < 1e-12) return { x: 0, y: 0 };
  const r = radius(theta, p.method);
  return { x: (r * w[0]) / h, y: (r * w[1]) / h };
}

/** Inverse projection. Points outside the primitive are clamped to it. */
export function unproject(pt: XY, p: Projection): Vec3 {
  let r = Math.hypot(pt.x, pt.y);
  if (r < 1e-12) return p.hemisphere === "lower" ? [0, 0, -1] : [0, 0, 1];
  const rc = Math.min(r, 1);
  const theta = thetaOf(rc, p.method);
  const s = Math.sin(theta), c = Math.cos(theta);
  const e = (s * pt.x) / r, n = (s * pt.y) / r;
  return [e, n, p.hemisphere === "lower" ? -c : c];
}
